/**
 * Safe access to browser localStorage.
 *
 * Storage can throw (private mode, quota, blocked cookies/storage policies),
 * so presentation/infrastructure code should not talk to localStorage directly.
 */
function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readStorage(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

/** For user-created data: do not mistake an unreadable value for an empty store. */
export function readStorageStrict(key: string): string | null {
  const target = storage();
  if (!target) throw new Error('O armazenamento deste navegador não está disponível.');
  return target.getItem(key);
}

/** Lists only the requested namespace; callers decide when to expose legacy data. */
export function listStorageKeys(prefix: string): string[] {
  const target = storage();
  if (!target) throw new Error('O armazenamento deste navegador não está disponível.');
  const keys: string[] = [];
  for (let index = 0; index < target.length; index++) {
    const key = target.key(index);
    if (key?.startsWith(prefix)) keys.push(key);
  }
  return keys.sort();
}

export function writeStorage(key: string, value: string): boolean {
  try {
    const target = storage();
    if (!target) return false;
    target.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

export function removeStorage(key: string): boolean {
  try {
    const target = storage();
    if (!target) return false;
    target.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

export function readStorageJson(key: string): unknown | null {
  const raw = readStorage(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

export function writeStorageJson(key: string, value: unknown): boolean {
  try {
    return writeStorage(key, JSON.stringify(value));
  } catch {
    return false;
  }
}

/** Reads current key, or migrates the first available legacy key once. */
export function readMigratedStorage(
  currentKey: string,
  legacyKeys: readonly string[],
): string | null {
  const current = readStorage(currentKey);
  if (current != null) return current;

  for (const legacyKey of legacyKeys) {
    const legacy = readStorage(legacyKey);
    if (legacy == null) continue;
    if (writeStorage(currentKey, legacy)) removeStorage(legacyKey);
    return legacy;
  }
  return null;
}
